// Loxa Bundler Integration — eCatering
// Adds warranty/insurance badges to products inside the Bundler app widget.
// On bundle ATC, injects Loxa properties onto each insurable item via XHR intercept.
//
// Dependencies: LoxaShopify (main embed must be loaded), Bundler app widget (.bndlr-container)
// Cart mode: bundled (cart transform) — properties on the line item are sufficient.

(function () {
  // ── Feature flags ──────────────────────────────────────────────────────────
  // SIDEBAR_MODE:
  //   "warranty" — sidebar shows warranty info + opt-out only (no extension upsell)
  //   "full"     — sidebar shows radio options: base warranty, extension, remove
  var SIDEBAR_MODE = "full";

  // BUNDLE_CTA_ENABLED:
  //   When true, shows a single "Add N Year Protection to Bundle" CTA below all
  //   bundle products that lets users opt-in to long-term protection across
  //   the whole bundle in one click. Per-product badges still work independently.
  var BUNDLE_CTA_ENABLED = true;

  // The term used for the bundle CTA. Auto-detected as the longest term
  // available across all products if null, otherwise locked to this value.
  var BUNDLE_CTA_TERM = null; // e.g. 5 to lock to 5 years, null to auto-detect

  // Bundle CTA requires the full sidebar flow (extensions/addons) to work
  // properly. If the bundle is enabled, override SIDEBAR_MODE to "full".
  if (BUNDLE_CTA_ENABLED && SIDEBAR_MODE === "warranty") {
    SIDEBAR_MODE = "full";
  }

  var MAX_ATTEMPTS = 50;
  var POLL_DELAY = 200;
  var attempts = 0;

  function waitForDependencies() {
    // Wait for LoxaShopify to be available with its proxy path helper
    if (typeof LoxaShopify === "undefined" || !LoxaShopify._getProxyPath) {
      attempts++;
      if (attempts >= MAX_ATTEMPTS) return;
      setTimeout(waitForDependencies, POLL_DELAY);
      return;
    }

    // Wait for bundler widget to render
    var bundlerEl = document.querySelector(".bndlr-container");
    if (!bundlerEl) {
      attempts++;
      if (attempts >= MAX_ATTEMPTS) return;
      setTimeout(waitForDependencies, POLL_DELAY);
      return;
    }

    boot(bundlerEl);
  }

  // ── State ──────────────────────────────────────────────────────────────────

  // Per-product insurance state keyed by variant ID string.
  // {
  //   variantId: { insurable, insuranceData, selectedCode, optedOut,
  //                productTitle, price, integrationType, badgeEl }
  // }
  var state = {};

  // ── Boot ───────────────────────────────────────────────────────────────────

  function boot(bundlerEl) {
    var L = LoxaShopify;
    L.log("Bundler integration: initialising", {
      bundle: bundlerEl.getAttribute("data-bundle-name"),
    });

    var products = bundlerEl.querySelectorAll(".bndlr-product");
    if (!products.length) return;

    var settings = L._getSettings() || {};

    // Fetch insurance for each product in parallel
    var fetches = [];
    for (var i = 0; i < products.length; i++) {
      fetches.push(fetchProductInsurance(products[i], settings));
    }

    // Once all fetches resolve, render badges and install interceptor
    Promise.all(fetches).then(function () {
      var insurable = Object.keys(state).filter(function (vid) {
        return state[vid].insurable;
      });
      L.log("Bundler integration: insurance fetched", {
        total: products.length,
        insurable: insurable.length,
      });

      if (insurable.length > 0) {
        installXhrInterceptor();

        // Render bundle CTA badge if feature flag enabled
        if (BUNDLE_CTA_ENABLED) {
          renderBundleCta(bundlerEl);
        }
      }
    });
  }

  // ── Fetch insurance for a single product ───────────────────────────────────

  function fetchProductInsurance(productEl, settings) {
    var L = LoxaShopify;
    var productId = productEl.getAttribute("data-product-id");
    var selectEl = productEl.querySelector("select");
    var variantId = selectEl ? selectEl.value : null;
    if (!variantId) {
      L.log("Bundler integration: no variant ID for product " + productId);
      return Promise.resolve();
    }

    var titleEl = productEl.querySelector(".bndlr-product-title");
    var productTitle = titleEl ? titleEl.textContent.trim() : "";

    var priceEl = productEl.querySelector(
      ".bndlr-product-price span[data-currentprice]",
    );
    var pricePence = priceEl
      ? parseInt(priceEl.getAttribute("data-currentprice"), 10)
      : 0;
    var vatApplied = priceEl
      ? priceEl.getAttribute("data-vat-applied") === "true"
      : false;

    // Convert pence to pounds
    var pricePounds = pricePence / 100;

    // If VAT was not applied by the bundler, apply 20% UK VAT for the API call
    if (!vatApplied && settings.vatExcluded) {
      pricePounds = pricePounds * 1.2;
    }

    // If VAT was applied but the store setting says prices exclude VAT,
    // the bundler price is already inclusive — use as-is
    // If VAT was applied and store doesn't exclude — also use as-is

    var proxyPath = L._getProxyPath();
    var shopRoot = (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/"
    ).replace(/\/$/, "/");

    var url;
    try {
      var base = new URL(proxyPath, window.location.origin + shopRoot);
      base.searchParams.set("product_sku", variantId);
      base.searchParams.set("product_title", productTitle);
      base.searchParams.set("product_price", pricePounds.toString());
      url = base.toString();
    } catch (e) {
      L.error("Bundler integration: failed to build proxy URL", e);
      return Promise.resolve();
    }

    return fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    })
      .then(function (resp) {
        if (!resp.ok) throw new Error("HTTP " + resp.status);
        return resp.json();
      })
      .then(function (data) {
        if (!data || !data.insurable || !data.active) {
          state[variantId] = { insurable: false };
          return;
        }

        var insurances = (data.insurances || []).slice();
        var integrationType = data.integration_type || null;

        // Sort plans so the sidebar lists them by term ascending.
        // Base warranty/inclusive plans always come first, then extensions/addons by term.
        insurances.sort(function (a, b) {
          var aBase = a.is_base_insurance_product === true ? 0 : 1;
          var bBase = b.is_base_insurance_product === true ? 0 : 1;
          if (aBase !== bBase) return aBase - bBase;
          var aTerm = parseInt(a.insurance_term || a.term || "0", 10);
          var bTerm = parseInt(b.insurance_term || b.term || "0", 10);
          return aTerm - bTerm;
        });

        // Find the base warranty/inclusive plan
        var basePlan = findBasePlan(insurances, integrationType);
        var selectedCode = basePlan ? basePlan.code : null;

        state[variantId] = {
          insurable: true,
          insuranceData: data,
          insurances: insurances,
          integrationType: integrationType,
          selectedCode: selectedCode,
          optedOut: false,
          variantId: variantId,
          productTitle: productTitle,
          price: pricePounds,
          category: data.insurance_category || "",
          badgeEl: null,
        };

        renderBadge(productEl, variantId);
      })
      .catch(function (err) {
        L.error(
          "Bundler integration: insurance fetch failed for variant " +
            variantId,
          err,
        );
        state[variantId] = { insurable: false };
      });
  }

  // ── Find the base (warranty/inclusive) plan ─────────────────────────────────

  function findBasePlan(insurances, integrationType) {
    if (integrationType === "hybrid_warranty") {
      return (
        insurances.find(function (ins) {
          return (
            ins.pricing_type === "warranty" &&
            ins.is_base_insurance_product === true
          );
        }) ||
        insurances.find(function (ins) {
          return ins.pricing_type === "warranty";
        }) ||
        null
      );
    }
    if (integrationType === "hybrid_extension") {
      return (
        insurances.find(function (ins) {
          return (
            (ins.pricing_type === "inclusive" ||
              ins.inclusive_insurance === true) &&
            ins.is_base_insurance_product === true
          );
        }) ||
        insurances.find(function (ins) {
          return (
            ins.pricing_type === "inclusive" || ins.inclusive_insurance === true
          );
        }) ||
        null
      );
    }
    if (integrationType === "inclusive") {
      return (
        insurances.find(function (ins) {
          return (
            ins.pricing_type === "inclusive" || ins.inclusive_insurance === true
          );
        }) || null
      );
    }
    // addon — no base plan
    return null;
  }

  // ── Render badge ───────────────────────────────────────────────────────────
  // Compact inline pill: [Loxa logo] Free 2 Year Warranty  See details
  // Uses .loxa-inclusive-badge colour via .loxa-bundler-pill class.

  var LOXA_LOGO_URL =
    "https://bolt-cover-assets.s3.eu-west-1.amazonaws.com/logo/LOXA_Stacked+Logo-Lime+on+Black.svg";

  function renderBadge(productEl, variantId) {
    var s = state[variantId];
    if (!s || !s.insurable) return;

    var basePlan = findBasePlan(s.insurances, s.integrationType);
    var isAddonOnly = !basePlan;

    // In warranty mode, addon-only products don't show a badge.
    if (isAddonOnly && SIDEBAR_MODE === "warranty") return;

    // Remove existing badge if re-rendering
    if (s.badgeEl && s.badgeEl.parentNode) {
      s.badgeEl.parentNode.removeChild(s.badgeEl);
    }

    var badge = document.createElement("div");
    badge.className = "loxa-bundler-badge";
    badge.setAttribute("data-loxa-bundler-variant", variantId);

    if (isAddonOnly) {
      // ── Addon flow: no base warranty, just paid plans ──
      var selectedAddon =
        s.selectedCode && s.selectedCode !== "__none"
          ? s.insurances.find(function (ins) {
              return ins.code === s.selectedCode;
            })
          : null;

      if (selectedAddon) {
        var addonTerm =
          selectedAddon.insurance_term || selectedAddon.term || "";
        var addonTitle = addonTerm + " Year Protection Added";
        badge.innerHTML =
          '<div class="loxa-bundler-pill loxa-bundler-pill--addon-active">' +
          '<img src="' +
          LOXA_LOGO_URL +
          '" alt="Loxa" class="loxa-bundler-logo" />' +
          '<span class="loxa-bundler-label">' +
          escapeHtml(addonTitle) +
          "</span>" +
          '<button type="button" class="loxa-bundler-link">Change</button>' +
          "</div>";
      } else {
        badge.innerHTML =
          '<div class="loxa-bundler-pill loxa-bundler-pill--addon-available">' +
          '<img src="' +
          LOXA_LOGO_URL +
          '" alt="Loxa" class="loxa-bundler-logo" />' +
          '<span class="loxa-bundler-label">Protection available</span>' +
          '<button type="button" class="loxa-bundler-link">See details</button>' +
          "</div>";
      }
    } else {
      // ── Warranty / hybrid flow ──
      var activePlan = getActivePlan(s) || basePlan;
      var term = activePlan.insurance_term || activePlan.term || "";
      var isExtension =
        activePlan.pricing_type === "extension" ||
        activePlan.extension === true;
      var title = isExtension
        ? "Extended " + term + " Year Warranty"
        : "Free " + term + " Year Warranty";

      if (s.optedOut) {
        badge.innerHTML =
          '<div class="loxa-bundler-pill loxa-bundler-pill--off">' +
          '<img src="' +
          LOXA_LOGO_URL +
          '" alt="Loxa" class="loxa-bundler-logo" />' +
          '<span class="loxa-bundler-label">Warranty Removed</span>' +
          '<button type="button" class="loxa-bundler-link">Add back</button>' +
          "</div>";
      } else {
        var linkLabel = isExtension ? "Change" : "See details";
        badge.innerHTML =
          '<div class="loxa-bundler-pill">' +
          '<img src="' +
          LOXA_LOGO_URL +
          '" alt="Loxa" class="loxa-bundler-logo" />' +
          '<span class="loxa-bundler-label">' +
          escapeHtml(title) +
          "</span>" +
          '<button type="button" class="loxa-bundler-link">' +
          linkLabel +
          "</button>" +
          "</div>";
      }
    }

    var linkEl = badge.querySelector(".loxa-bundler-link");

    // When the bundle CTA is enabled, per-product links are hidden — all
    // interaction happens at the bundle level. Badges still show the state.
    if (BUNDLE_CTA_ENABLED && linkEl) {
      linkEl.style.display = "none";
    } else if (linkEl) {
      linkEl.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        // In warranty mode, "Add back" just restores the warranty — no sidebar needed
        if (!isAddonOnly && s.optedOut && SIDEBAR_MODE === "warranty") {
          var bp = findBasePlan(s.insurances, s.integrationType);
          s.optedOut = false;
          s.selectedCode = bp ? bp.code : null;
          renderBadge(productEl, variantId);
          return;
        }
        openBundlerSidebar(variantId);
      });
    }

    // Insert after the price element
    var priceEl = productEl.querySelector(".bndlr-product-price");
    if (priceEl && priceEl.parentNode) {
      priceEl.parentNode.insertBefore(badge, priceEl.nextSibling);
    } else {
      productEl.appendChild(badge);
    }

    s.badgeEl = badge;
  }

  // ── Bundle CTA ─────────────────────────────────────────────────────────────
  // A single badge below the bundle that lets users opt-in to long-term
  // protection across all eligible products in one click.

  var _bundleBadgeEl = null;
  var _bundleCtaTerm = null; // resolved term used for the CTA (e.g. 5)

  // Find the longest extension/addon term available across all insurable products.
  function resolveBundleCtaTerm() {
    if (BUNDLE_CTA_TERM != null) return BUNDLE_CTA_TERM;

    var maxTerm = 0;
    Object.keys(state).forEach(function (vid) {
      var s = state[vid];
      if (!s.insurable) return;
      s.insurances.forEach(function (ins) {
        var isExt =
          ins.pricing_type === "extension" ||
          ins.extension === true ||
          ins.pricing_type === "addon";
        if (!isExt) return;
        var t = parseInt(ins.insurance_term || ins.term || "0", 10);
        if (t > maxTerm) maxTerm = t;
      });
    });
    return maxTerm > 0 ? maxTerm : null;
  }

  // For a given product state, find the extension/addon plan matching the term.
  function findBundleCtaPlan(s, term) {
    if (!s || !s.insurable || !term) return null;
    return (
      s.insurances.find(function (ins) {
        var t = parseInt(ins.insurance_term || ins.term || "0", 10);
        var isExt =
          ins.pricing_type === "extension" ||
          ins.extension === true ||
          ins.pricing_type === "addon";
        return isExt && t === term;
      }) || null
    );
  }

  // Calculate the total price for the bundle CTA across eligible products.
  function calculateBundleCtaTotal(term) {
    var total = 0;
    Object.keys(state).forEach(function (vid) {
      var s = state[vid];
      var plan = findBundleCtaPlan(s, term);
      if (!plan) return;
      var price =
        typeof plan.insurance_price === "number"
          ? plan.insurance_price
          : parseFloat(plan.insurance_price || "0");
      if (!isNaN(price)) total += price;
    });
    return total;
  }

  // Check if all eligible products are currently using the bundle CTA plan.
  function isBundleCtaActive(term) {
    if (!term) return false;
    var hasEligible = false;
    var allMatch = true;
    Object.keys(state).forEach(function (vid) {
      var s = state[vid];
      var plan = findBundleCtaPlan(s, term);
      if (!plan) return;
      hasEligible = true;
      if (s.optedOut || s.selectedCode !== plan.code) allMatch = false;
    });
    return hasEligible && allMatch;
  }

  function renderBundleCta(bundlerEl) {
    var L = LoxaShopify;
    _bundleCtaTerm = resolveBundleCtaTerm();
    if (!_bundleCtaTerm) {
      L.log("Bundler integration: no eligible bundle CTA term — hiding CTA");
      return;
    }

    // Remove existing bundle badge if re-rendering
    if (_bundleBadgeEl && _bundleBadgeEl.parentNode) {
      _bundleBadgeEl.parentNode.removeChild(_bundleBadgeEl);
    }

    var total = calculateBundleCtaTotal(_bundleCtaTerm);
    var active = isBundleCtaActive(_bundleCtaTerm);

    var titleText = "Add " +
      _bundleCtaTerm +
      " Year Product Protection - \u00a3" +
      total.toFixed(2);
    var descriptionText =
      "Protects your bundle against accidental damage and unexpected faults.";

    var badge = document.createElement("div");
    badge.className = "loxa-bundler-cta-badge";
    badge.setAttribute("data-loxa-bundler-cta", "true");

    badge.innerHTML =
      '<div class="loxa-card loxa-add-on-badge loxa-bundler-cta-card' +
      (active ? " loxa-bundler-cta-card--active" : "") +
      '">' +
      '<div class="loxa-add-on-inner">' +
      '<div class="loxa-add-on-main">' +
      '<input type="checkbox" class="loxa-bundler-cta-checkbox" ' +
      (active ? "checked" : "") +
      " />" +
      '<img src="' +
      LOXA_LOGO_URL +
      '" alt="Loxa" class="loxa-add-on-logo" width="30" height="34" />' +
      '<span class="loxa-add-on-title"></span>' +
      "</div>" +
      '<p class="loxa-add-on-description">' +
      "<span></span> " +
      '<button type="button" class="loxa-learn-more-link loxa-bundler-cta-details">' +
      '<span class="loxa-learn-more-link-text">See Details</span>' +
      "</button>" +
      "</p>" +
      "</div>" +
      "</div>";

    badge.querySelector(".loxa-add-on-title").textContent = titleText;
    badge.querySelector(".loxa-add-on-description span").textContent =
      descriptionText;

    // Checkbox toggles the bundle add/remove
    var checkbox = badge.querySelector(".loxa-bundler-cta-checkbox");
    if (checkbox) {
      checkbox.addEventListener("change", function (e) {
        e.stopPropagation();
        if (checkbox.checked) {
          applyBundleCta();
        } else {
          removeBundleCta();
        }
      });
    }

    // "See Details" opens the sidebar
    var detailsBtn = badge.querySelector(".loxa-bundler-cta-details");
    if (detailsBtn) {
      detailsBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        openBundleCtaSidebar();
      });
    }

    // Card click (excluding the details link) toggles the checkbox
    badge.addEventListener("click", function (e) {
      if (
        e.target === detailsBtn ||
        (detailsBtn && detailsBtn.contains(e.target))
      ) {
        return;
      }
      if (e.target === checkbox) return;
      if (!checkbox) return;
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change"));
    });

    // Insert above the .bndlr-total-price
    var totalEl = bundlerEl.querySelector(".bndlr-total-price");
    if (totalEl && totalEl.parentNode) {
      totalEl.parentNode.insertBefore(badge, totalEl);
    } else {
      bundlerEl.appendChild(badge);
    }
    _bundleBadgeEl = badge;
  }

  // Apply the bundle CTA — set every eligible product to the matching plan.
  function applyBundleCta() {
    var term = _bundleCtaTerm;
    if (!term) return;

    Object.keys(state).forEach(function (vid) {
      var s = state[vid];
      var plan = findBundleCtaPlan(s, term);
      if (!plan) return;
      s.selectedCode = plan.code;
      s.optedOut = false; // bundle add overrides individual opt-outs
      // Re-render the per-product badge
      var productEl = s.badgeEl ? s.badgeEl.closest(".bndlr-product") : null;
      if (productEl) {
        renderBadge(productEl, vid);
      }
    });

    // Re-render the bundle badge to reflect active state
    var bundlerEl = document.querySelector(".bndlr-container");
    if (bundlerEl) renderBundleCta(bundlerEl);
  }

  // Remove the bundle CTA — revert addon products to no selection,
  // and warranty products back to base warranty (keep the free warranty active).
  function removeBundleCta() {
    Object.keys(state).forEach(function (vid) {
      var s = state[vid];
      if (!s.insurable) return;

      var basePlan = findBasePlan(s.insurances, s.integrationType);
      if (basePlan) {
        // Warranty product — revert to base warranty (extension removed, warranty stays)
        s.selectedCode = basePlan.code;
        s.optedOut = false;
      } else {
        // Addon-only product — clear selection (no insurance)
        s.selectedCode = null;
        s.optedOut = false;
      }

      var productEl = s.badgeEl ? s.badgeEl.closest(".bndlr-product") : null;
      if (productEl) {
        renderBadge(productEl, vid);
      }
    });

    var bundlerEl = document.querySelector(".bndlr-container");
    if (bundlerEl) renderBundleCta(bundlerEl);
  }

  // Find a representative plan to use as a template for the synthetic bundle plan.
  // Prefers an addon plan (so IPID, policy wording links, and content come from
  // the consumer addon product rather than a warranty extension). Falls back to
  // any matching extension/addon plan.
  function findBundleCtaTemplatePlan(term) {
    var keys = Object.keys(state);
    var fallback = null;
    for (var i = 0; i < keys.length; i++) {
      var s = state[keys[i]];
      if (!s.insurable) continue;
      var plan = findBundleCtaPlan(s, term);
      if (!plan) continue;
      if (plan.pricing_type === "addon") return plan;
      if (!fallback) fallback = plan;
    }
    return fallback;
  }

  // Open the existing addon sidebar with a synthetic plan representing the
  // whole bundle. The plan is a clone of a real plan with adjusted price/title.
  function openBundleCtaSidebar() {
    var L = LoxaShopify;
    var term = _bundleCtaTerm;
    if (!term) return;

    var template = findBundleCtaTemplatePlan(term);
    if (!template) {
      L.error("Bundle CTA: no template plan found for term " + term);
      return;
    }

    var total = calculateBundleCtaTotal(term);
    var active = isBundleCtaActive(term);

    // Synthetic plan — identifies as the bundle CTA via a special code prefix.
    var bundleCode = "__loxa_bundle_cta_" + term;
    var bundlePlan = JSON.parse(JSON.stringify(template));
    bundlePlan.code = bundleCode;
    bundlePlan.insurance_price = total;
    bundlePlan.insurance_term = term;
    bundlePlan.term = term;
    bundlePlan.name = term + " Year Bundle Protection";
    bundlePlan.is_base_insurance_product = false;
    bundlePlan.extension = false;
    bundlePlan.base_insurance_product_code = null;
    bundlePlan.pricing_type = "addon";
    bundlePlan.default_selected = active;

    // Tweak insurance_content so the sidebar copy reflects the bundle context.
    // Description always uses the standard addon copy regardless of which
    // plan was used as the template (warranty/extension/addon).
    if (bundlePlan.insurance_content) {
      bundlePlan.insurance_content.title =
        "Add " + term + " year product protection to your bundle";
      bundlePlan.insurance_content.description =
        "Add Product Protection from Loxa for extra peace of mind, helping protect your appliance from everyday accidents and unexpected faults.";
      if (bundlePlan.insurance_content.sidebar_content) {
        bundlePlan.insurance_content.sidebar_content.header =
          term + " Year Protection for your bundle";
        bundlePlan.insurance_content.sidebar_content.subheading =
          "Add Product Protection from Loxa for extra peace of mind, helping protect your appliance from everyday accidents and unexpected faults.";
      }
    }

    // Save state to restore after sidebar closes
    var prevIntegrationType = L.integrationType;
    var prevInsuranceProducts = L.insuranceProducts;
    var prevLastInsuranceData = L.lastInsuranceData;
    var prevSelectedCode = L.selectedInsuranceCode;
    var prevInclusiveOptOut = L.inclusiveOptOut;

    // Open the addon sidebar with just the synthetic bundle plan
    L.integrationType = "addon";
    L.insuranceProducts = [bundlePlan];
    L.lastInsuranceData = {
      insurances: [bundlePlan],
      integration_type: "addon",
      insurable: true,
      active: true,
    };
    L.selectedInsuranceCode = active ? bundleCode : "__none";
    L.inclusiveOptOut = false;

    L.openSidebar(L.lastInsuranceData);

    // Inject a "No thanks" secondary button after the sidebar renders.
    // Only show it when adding (no remove option present yet).
    setTimeout(function () {
      var actionsEl = document.querySelector(
        "#loxa-shopify-sidebar-content .loxa-sidebar-actions",
      );
      if (!actionsEl) return;
      if (actionsEl.querySelector(".loxa-bundler-cta-no-thanks")) return;

      // Skip if a visible "Remove Protection" link is already shown
      var removeBtn = actionsEl.querySelector(".loxa-sidebar-remove");
      var removeVisible =
        removeBtn &&
        removeBtn.offsetParent !== null &&
        removeBtn.style.display !== "none";
      if (removeVisible) return;

      var noThanksBtn = document.createElement("button");
      noThanksBtn.type = "button";
      noThanksBtn.className = "loxa-bundler-cta-no-thanks";
      noThanksBtn.textContent = "No thanks";
      noThanksBtn.addEventListener("click", function (e) {
        e.preventDefault();
        L.closeSidebar();
      });
      actionsEl.appendChild(noThanksBtn);
    }, 50);

    // Watch for sidebar close — apply or remove based on what the user picked
    var sidebarEl = document.getElementById("loxa-shopify-sidebar");
    if (!sidebarEl) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (
          mutations[i].type === "attributes" &&
          mutations[i].attributeName === "style"
        ) {
          var isOpen =
            sidebarEl.style.right === "0px" ||
            sidebarEl.style.right === "0" ||
            sidebarEl.style.left === "0px" ||
            sidebarEl.style.left === "0";
          if (!isOpen) {
            // Capture user's choice
            var picked = L.selectedInsuranceCode;
            if (picked === bundleCode) {
              applyBundleCta();
            } else if (active && (picked === "__none" || !picked)) {
              // Was active, user removed it
              removeBundleCta();
            }

            // Restore main embed state
            L.integrationType = prevIntegrationType;
            L.insuranceProducts = prevInsuranceProducts;
            L.lastInsuranceData = prevLastInsuranceData;
            L.selectedInsuranceCode = prevSelectedCode;
            L.inclusiveOptOut = prevInclusiveOptOut;

            observer.disconnect();
          }
        }
      }
    });
    observer.observe(sidebarEl, {
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  // ── Sidebar ────────────────────────────────────────────────────────────────

  // Track which variant the sidebar is currently showing
  var _sidebarVariantId = null;

  function openBundlerSidebar(variantId) {
    var L = LoxaShopify;
    var s = state[variantId];
    if (!s || !s.insurable) return;

    _sidebarVariantId = variantId;

    var basePlan = findBasePlan(s.insurances, s.integrationType);
    var isAddonOnly = !basePlan;

    // Save LoxaShopify state to restore after sidebar closes
    var prevIntegrationType = L.integrationType;
    var prevInsuranceProducts = L.insuranceProducts;
    var prevLastInsuranceData = L.lastInsuranceData;
    var prevSelectedCode = L.selectedInsuranceCode;
    var prevInclusiveOptOut = L.inclusiveOptOut;

    // Temporarily override LoxaShopify state so sidebar rendering works
    L.integrationType = s.integrationType;
    L.insuranceProducts = s.insurances.slice();
    L.inclusiveOptOut = false;

    if (isAddonOnly) {
      // Addon flow — use the standard addon sidebar from the main embed.
      // It already supports add/switch/remove, so no patching needed.
      L.selectedInsuranceCode = s.selectedCode || "__none";
      L.lastInsuranceData = {
        insurances: s.insurances,
        integration_type: s.integrationType,
        insurable: true,
        active: true,
        insurance_category: s.category,
      };
      L.openSidebar(L.lastInsuranceData);
    } else if (SIDEBAR_MODE === "warranty") {
      // Warranty-only mode — use the built-in inclusive details sidebar.
      // Shows warranty info, "Back to Product", and opt-out link.
      // Set selectedInsuranceCode to "__none" so the opt-out link is visible
      // (the main embed hides it when it thinks an extension is selected).
      L.selectedInsuranceCode = "__none";
      L.lastInsuranceData = {
        insurances: s.insurances,
        integration_type: s.integrationType,
        __inclusiveDetails: true,
        inclusivePlan: basePlan,
      };
      L.openSidebar(L.lastInsuranceData);
    } else {
      // Full mode — show radio options for base warranty + extensions + remove
      L.lastInsuranceData = {
        insurances: s.insurances,
        integration_type: s.integrationType,
        insurable: true,
        active: true,
        insurance_category: s.category,
      };
      if (s.optedOut) {
        L.selectedInsuranceCode = basePlan ? basePlan.code : "__none";
      } else if (s.selectedCode && s.selectedCode !== "__none") {
        L.selectedInsuranceCode = s.selectedCode;
      } else {
        L.selectedInsuranceCode = basePlan ? basePlan.code : "__none";
      }
      L.openSidebar(L.lastInsuranceData);

      // Inject the base warranty radio and rewire CTA/remove handlers
      setTimeout(function () {
        patchSidebarForBundler(variantId, basePlan, s, L);
      }, 50);
    }

    // Listen for sidebar close to capture the selection and restore state
    var sidebarEl = document.getElementById("loxa-shopify-sidebar");
    if (sidebarEl) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (
            mutations[i].type === "attributes" &&
            mutations[i].attributeName === "style"
          ) {
            var isOpen =
              sidebarEl.style.right === "0px" ||
              sidebarEl.style.right === "0" ||
              sidebarEl.style.left === "0px" ||
              sidebarEl.style.left === "0";
            if (!isOpen && _sidebarVariantId === variantId) {
              captureSelection(variantId, L);
              L.integrationType = prevIntegrationType;
              L.insuranceProducts = prevInsuranceProducts;
              L.lastInsuranceData = prevLastInsuranceData;
              L.selectedInsuranceCode = prevSelectedCode;
              L.inclusiveOptOut = prevInclusiveOptOut;
              _sidebarVariantId = null;
              observer.disconnect();
            }
          }
        }
      });
      observer.observe(sidebarEl, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }
  }

  function captureSelection(variantId, L) {
    var s = state[variantId];
    if (!s) return;

    s.selectedCode = L.selectedInsuranceCode;
    s.optedOut = L.inclusiveOptOut || L.selectedInsuranceCode === "__none";

    // If a paid extension was selected, it's not an opt-out
    if (s.selectedCode && s.selectedCode !== "__none") {
      var selectedPlan = s.insurances.find(function (ins) {
        return ins.code === s.selectedCode;
      });
      if (selectedPlan) {
        var price =
          typeof selectedPlan.insurance_price === "number"
            ? selectedPlan.insurance_price
            : parseFloat(selectedPlan.insurance_price || "0");
        if (
          price > 0 ||
          selectedPlan.pricing_type === "warranty" ||
          selectedPlan.inclusive_insurance
        ) {
          s.optedOut = false;
        }
      }
    }

    // Re-render the badge to reflect current state
    var productEl = s.badgeEl ? s.badgeEl.closest(".bndlr-product") : null;
    if (productEl) {
      renderBadge(productEl, variantId);
    }
  }

  // ── Sidebar patch ───────────────────────────────────────────────────────────
  // Injects the base warranty as a radio option at the top of the sidebar,
  // replaces the CTA/remove handlers to work with both base + extension options,
  // and ensures the remove button is always visible.

  function patchSidebarForBundler(_variantId, basePlan, s, L) {
    var content = document.getElementById("loxa-shopify-sidebar-content");
    if (!content || !basePlan) return;

    var optionsRoot = content.querySelector(".loxa-sidebar-content-root");
    if (!optionsRoot) return;

    // Check if base warranty option already exists (avoid duplicates on re-open)
    if (content.querySelector('[data-loxa-code="' + basePlan.code + '"]'))
      return;

    // Clone the option template used by the main sidebar
    var optionTemplate = document.getElementById(
      "loxa-sidebar-option-template",
    );
    if (!optionTemplate) return;

    var wrapper = optionTemplate.cloneNode(true);
    var optionEl = wrapper.querySelector(".loxa-sidebar-option");
    if (!optionEl) return;

    var baseTerm = basePlan.insurance_term || basePlan.term || "";
    var baseTermLabel = L.getTermLabel
      ? L.getTermLabel(basePlan)
      : baseTerm + "-year";

    optionEl.setAttribute("data-loxa-code", basePlan.code);

    var baseRadio = optionEl.querySelector('input[type="radio"]');
    if (baseRadio) {
      baseRadio.value = basePlan.code;
      baseRadio.name = "loxa-sidebar-insurance";
    }

    var titleSpan = optionEl.querySelector(".loxa-sidebar-option-title");
    if (titleSpan) {
      titleSpan.textContent = baseTermLabel + " warranty";
    }

    var priceSpan = optionEl.querySelector(".loxa-sidebar-option-price");
    if (priceSpan) {
      priceSpan.textContent = "Free";
    }

    // Insert as the first option (before extension options)
    var firstOption = optionsRoot.querySelector(".loxa-sidebar-option");
    if (firstOption) {
      optionsRoot.insertBefore(optionEl, firstOption);
    } else {
      optionsRoot.appendChild(optionEl);
    }

    // Determine initial selection
    var currentCode = s.optedOut
      ? basePlan.code
      : s.selectedCode && s.selectedCode !== "__none"
        ? s.selectedCode
        : basePlan.code;

    // Track the pending selection within the sidebar
    var pendingCode = currentCode;

    // Set initial radio state
    var allRadios = content.querySelectorAll(
      'input[name="loxa-sidebar-insurance"]',
    );
    allRadios.forEach(function (r) {
      r.checked = r.value === currentCode;
    });

    // Find CTA and remove buttons — replace with clones to strip old handlers
    var oldCta = content.querySelector(".loxa-sidebar-cta");
    var oldRemove = content.querySelector(".loxa-sidebar-remove");
    var ctaBtn = null;
    var removeBtn = null;

    if (oldCta) {
      ctaBtn = oldCta.cloneNode(true);
      oldCta.parentNode.replaceChild(ctaBtn, oldCta);
    }
    if (oldRemove) {
      removeBtn = oldRemove.cloneNode(true);
      oldRemove.parentNode.replaceChild(removeBtn, oldRemove);
      removeBtn.style.display = "inline-block";
    }

    // Helper: find plan by code across all insurances
    function findPlan(code) {
      if (!code) return null;
      return (
        s.insurances.find(function (ins) {
          return ins.code === code;
        }) || null
      );
    }

    // Update CTA label based on current pending selection
    function updateCta() {
      if (!ctaBtn) return;
      var plan = findPlan(pendingCode);
      var termLabel = plan && L.getTermLabel ? L.getTermLabel(plan) : "";
      var isExtension =
        plan && (plan.pricing_type === "extension" || plan.extension === true);
      var isBase = plan && plan.code === basePlan.code;

      if (isBase) {
        ctaBtn.textContent =
          "Keep " + (termLabel ? termLabel + " " : "") + "Warranty";
      } else if (isExtension) {
        ctaBtn.textContent =
          "Extend to " + (termLabel ? termLabel + " " : "") + "Protection";
      } else {
        ctaBtn.textContent = "Add Protection";
      }
    }

    // Wire all radios (base + extensions) to update pending selection
    allRadios.forEach(function (radio) {
      radio.addEventListener("change", function (e) {
        pendingCode = e.target.value;
        if (L.updateSidebarSelectionStyles) {
          L.updateSidebarSelectionStyles(content, pendingCode);
        }
        updateCta();
      });
    });

    // CTA click — apply selection and close
    if (ctaBtn) {
      ctaBtn.addEventListener("click", function () {
        L.selectedInsuranceCode = pendingCode;
        L.inclusiveOptOut = false;
        L.closeSidebar();
      });
    }

    // Remove click — opt out and close
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        L.selectedInsuranceCode = "__none";
        L.inclusiveOptOut = true;
        L.closeSidebar();
      });
    }

    // Initial CTA state
    if (L.updateSidebarSelectionStyles) {
      L.updateSidebarSelectionStyles(content, currentCode);
    }
    updateCta();
  }

  // ── XHR Interceptor ────────────────────────────────────────────────────────

  function installXhrInterceptor() {
    var L = LoxaShopify;

    if (window._loxaBundlerXhrInstalled) return;
    window._loxaBundlerXhrInstalled = true;

    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._loxaBundlerUrl = url;
      this._loxaBundlerMethod = method;
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      // Only intercept bundler cart-add requests
      if (
        this._loxaBundlerMethod &&
        this._loxaBundlerMethod.toUpperCase() === "POST" &&
        this._loxaBundlerUrl &&
        this._loxaBundlerUrl.indexOf("/cart/add") !== -1 &&
        this._loxaBundlerUrl.indexOf("bundler-cart-call") !== -1 &&
        typeof body === "string"
      ) {
        var modifiedBody = injectInsuranceProperties(body);
        L.log("Bundler integration: XHR intercepted, properties injected");
        return origSend.call(this, modifiedBody);
      }
      return origSend.apply(this, arguments);
    };

    L.log("Bundler integration: XHR interceptor installed");
  }

  // ── Inject properties into URL-encoded body ────────────────────────────────

  function injectInsuranceProperties(body) {
    var L = LoxaShopify;
    var decoded = decodeURIComponent(body);

    // Parse which item indices map to which variant IDs
    // Pattern: items[N][id]=VARIANT_ID
    var itemMap = {}; // { variantId: itemIndex }
    var regex = /items\[(\d+)\]\[id\]=(\d+)/g;
    var match;
    while ((match = regex.exec(decoded)) !== null) {
      itemMap[match[2]] = match[1];
    }

    var extra = "";

    for (var variantId in itemMap) {
      if (!itemMap.hasOwnProperty(variantId)) continue;
      var s = state[variantId];
      if (!s || !s.insurable || s.optedOut) continue;

      // In warranty mode, skip addon-only products (no base warranty).
      var basePlanForItem = findBasePlan(s.insurances, s.integrationType);
      if (!basePlanForItem && SIDEBAR_MODE === "warranty") continue;

      var idx = itemMap[variantId];
      var plan = getActivePlan(s);
      if (!plan) continue;

      var premium =
        typeof plan.insurance_price === "number"
          ? plan.insurance_price
          : parseFloat(plan.insurance_price || "0");

      // Insurance product variant ID — required by the cart transform to bundle
      var insProductId = L.insuranceProductIdNumeric || "";

      var props = {
        _loxaInsuranceCode: plan.code,
        "Loxa Insurance": plan.name || "Protection Plan",
        Premium: "\u00a3" + premium.toFixed(2), // £ sign
        _loxaSignature: plan.premium_signature || "",
        "Sum Insured": s.price ? "\u00a3" + s.price.toFixed(2) : "",
        _Category: s.category || "",
        _loxaInsuranceProductId: insProductId,
        _loxaTerm: String(plan.insurance_term || plan.term || ""),
      };

      // If hybrid with extension selected, also include the base plan code
      var basePlan = findBasePlan(s.insurances, s.integrationType);
      if (basePlan && plan.code !== basePlan.code) {
        props._loxaInclusiveCode = basePlan.code;
      }

      for (var key in props) {
        if (!props.hasOwnProperty(key)) continue;
        extra +=
          "&" +
          encodeURIComponent("items[" + idx + "][properties][" + key + "]") +
          "=" +
          encodeURIComponent(props[key]);
      }

      L.log(
        "Bundler integration: injecting properties for variant " + variantId,
        {
          plan: plan.code,
          premium: premium,
          index: idx,
        },
      );
    }

    return body + extra;
  }

  // ── Get the active plan for a product ──────────────────────────────────────

  function getActivePlan(s) {
    if (!s || !s.insurable || s.optedOut) return null;

    // If a specific code was selected (e.g. extension from sidebar), use that
    if (s.selectedCode && s.selectedCode !== "__none") {
      var selected = s.insurances.find(function (ins) {
        return ins.code === s.selectedCode;
      });
      if (selected) return selected;
    }

    // Default to the base warranty/inclusive plan
    return findBasePlan(s.insurances, s.integrationType);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function escapeHtml(str) {
    var div = document.createElement("div");
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // ── Start ──────────────────────────────────────────────────────────────────

  waitForDependencies();
})();
